import React, {useCallback, useEffect, useRef, useState} from 'react';
import PaginationComponent from "../../components/core/Pagination/PaginationComponent";
import {createWorkOrderForCompany, getWorkOrdersForCompany} from "../../API/companyApi";
import messageFromError from "../../utils/errorUtils";
import {toast} from "react-toastify";
import {Badge, Col, Container, FormControl, FormLabel, Row} from "react-bootstrap";
import MyTable from "../../components/core/Components/MyTable";
import ConditionalRender from "../../components/ConditionalRender";
import LoadingTableSpinner from "../../components/Loading/LoadingTableSpinner";
import useCompanyId from "../../Hooks/useCompanyId";
import IconButton from "../../components/IconButton";
import {faPlus} from "@fortawesome/free-solid-svg-icons";
import css from '../css/BrowseWorkOrdersPage.module.css'
import {useNavigate} from "react-router-dom";
import ActionModal from "../../components/Modals/ActionModals/ActionModal";
import NoElementsAlert from "../../components/NoElementsAlert";
import {useTranslation} from "react-i18next";

const BrowseWorkOrdersPage = () => {

    const companyId = useCompanyId()
    const navigate = useNavigate()
    const {t} = useTranslation()

    const [workOrdersPageable, setWorkOrdersPageable] = useState(null)
    const [showCreateWorkOrderModal, setShowCreateWorkOrderModal] = useState(false)
    const [workOrderName, setWorkOrderName] = useState('')
    const [workOrderKey, setWorkOrderKey] = useState('')

    const pageRef = useRef(0)

    const fetchAndSetWorkOrders = useCallback(async (page) => {
        try {
            const response = await getWorkOrdersForCompany(companyId, page)
            setWorkOrdersPageable(response.data)
            pageRef.current = page
        } catch (err) {
            toast.error(messageFromError(err))
        }
    }, [companyId])

    useEffect(() => {
        fetchAndSetWorkOrders(0)
    }, [companyId, fetchAndSetWorkOrders]);


    const TableHeader = () => {
        return (
            <div className={'d-flex justify-content-between align-items-center'}>
                <div className={'d-flex'}>
                    <h6 className='mb-0'>{t('pages.workOrders.title')}</h6>
                    <Badge pill className={'ms-2'}>{workOrdersPageable?.totalElements ?? 0}</Badge>
                </div>
                <IconButton icon={faPlus} color="white" bg="#198754" onClick={()=> setShowCreateWorkOrderModal(true)}/>
            </div>
        )
    }

    const TableFooter = useCallback(() => {
        return (
            <div>
                <PaginationComponent lastPage={workOrdersPageable.lastPage}
                                     className={'d-flex justify-content-center'}
                                     onPageChanged={(p) => fetchAndSetWorkOrders(p)}/>
            </div>
        )
    }, [fetchAndSetWorkOrders, workOrdersPageable?.lastPage])

    const WorkOrderRow = ({workOrder}) => {
        return (
            <tr className={css.tableRow} onClick={() => navigate(`/workorders/${workOrder.id}`)}>
                <td>{workOrder.key}</td>
                <td>{workOrder.name}</td>
                <td>{workOrder.assignedGeofences.length}</td>
            </tr>
        )
    }

    return (
        <Container className='px-4 py-2'>
            <Row className='justify-content-center'>
                    <Col md={8}>
                        <MyTable
                            Header={TableHeader}
                            Footer={workOrdersPageable && workOrdersPageable.totalPages > 1 ? TableFooter : null}>
                            <ConditionalRender condition={workOrdersPageable} fallback={<tbody><LoadingTableSpinner small/></tbody>}>
                                <thead>
                                    <tr>
                                        <th>{t('pages.workOrders.workOrderId')}</th>
                                        <th>{t('pages.workOrders.workOrderName')}</th>
                                        <th>{t('pages.workOrders.geofences')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {workOrdersPageable?.data.map(workOrder => <WorkOrderRow key={workOrder.key} workOrder={workOrder}/>)}
                                </tbody>
                            </ConditionalRender>
                        </MyTable>
                        <NoElementsAlert show={workOrdersPageable?.totalElements < 1}/>
                    </Col>
                </Row>

            <ActionModal
                func={async () => createWorkOrderForCompany(companyId, {name: workOrderName, key: workOrderKey})}
                active={showCreateWorkOrderModal}
                hideOnConfirm
                onCancel={() => {setShowCreateWorkOrderModal(false)}}
                onConfirm={()=> {
                    toast.success("Created");
                    setWorkOrderName('')
                    setWorkOrderKey('')
                    fetchAndSetWorkOrders(pageRef.current)
                }}
                title={t('pages.workOrders.newWorkOrderDialog.title')}
                positiveButton={{
                    variant: 'success',
                    disabled: workOrderKey.trim().length < 1
                }}>

                <FormLabel>{t('pages.workOrders.newWorkOrderDialog.name')}</FormLabel>
                <FormControl required type='text' value={workOrderName} placeholder={t('pages.workOrders.newWorkOrderDialog.namePlaceholder')} onChange={(e) => {setWorkOrderName(e.target.value)}} />
                <FormLabel className='mt-2'>{t('pages.workOrders.newWorkOrderDialog.key')} <span className={'text-danger'}>*</span></FormLabel>
                <FormControl required type='text' value={workOrderKey} placeholder={t('pages.workOrders.newWorkOrderDialog.keyPlaceholder')} onChange={(e) => {setWorkOrderKey(e.target.value)}} />
            </ActionModal>
        </Container>
    );
};

export default BrowseWorkOrdersPage;